/**
 * Created by rastwe on 1/13/2017.
 * @module $Name$
 * @extends React.Component
 */
/* eslint react/no-multi-comp: 0*/
import './app.less';
import './globals.less';
import React, { useEffect, useState, Fragment, useMemo, lazy, Suspense, useRef } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { hot } from 'react-hot-loader';
import { useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { getTheme } from '@ey/styled-theme';
import Errors from '../common/components/Errors/Errors';
import LoaderContainer from '../common/components/LoaderContainer/LoaderContainer';
import Main from '../common/components/Main/Main';
import Nav from '../common/components/Nav/Nav';
import { getUserById } from '../features/users/users';
import {
	Theme,
	routesResources,
	loadingAreas
} from '../common/utils/uiconstants';
import { raiseJSValidationError } from './../common/errors';
import { getUserById as getAdminUserById } from '../features/admin/admin';
import { useLocation } from 'react-router-dom';
import { useDispatchAction } from '../common/utils/customHooks';
import { selectArea } from '../common/selectors/loading-selector';
import {
	adobeAnalytics
} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import { pageNames, siteSections } from '../common/components/AdobeAnalytics/constants';
import {pageViewArgs} from '../common/components/AdobeAnalytics/actionHelper';

const adobeAnalyticsEnabled = envConfig.adobeAnalytics?.enabled;

/* the import() call must be located in this outer function. Placing it
 * inside load() will cause Webpack to raise a warning about a required
 * file path being an expression.
 * The value of the component={} attribute must be a function or a
 * React component. React-Router sends the props in as the first parameter,
 * so grab those and forward them to the Async.load() as the first
 * argument. The second argument must be an import() statement. */

const DryDock = lazy(() => import('../common/components/DryDock/DryDock'));
const UserSettings = lazy(() => import('../features/users/components/UserSettings/UserSettings'));
const Projects = lazy(() => import('../features/projects/components/Project/Project'));
const Project = lazy(() => import('../features/projects/components/ProjectDetail/ProjectDetail'));
const ProjectSummary = lazy(() => import('../features/projectSummary/components/ProjectSummary/projectSummary'));
const AdminPortal = lazy(() => import('../features/admin/components/Admin/Admin'));


const adminRoute = {
	path: `/${routesResources.admin}/:cardId?/:partitionKey?`
};

const App = () => {
	const { pathname } = useLocation();

	const dispatch = useDispatchAction([loadingAreas.app]);

	const isLoading = useSelector(selectArea(loadingAreas.app));

	const isAdminPageRequest = useMemo(() => {
		return pathname.toLowerCase().includes(`/${routesResources.admin}`);
	}, [pathname]);

	const user = useSelector((store) =>
		isAdminPageRequest ? store.admin.user : store.user
	);

	const [redirectToSettings, setRedirectToSettings] = useState(false);
	const [hasMounted, setHasMounted] = useState(false);
	const [routes, setRoutes] = useState([]);

	const userRef = useRef();

	const setUserTheme = () => {
		const body = document.getElementsByTagName('body');
		body[0].setAttribute('motif-theme', user?.theme ?? Theme.Light);
	};

	useEffect(() => {
		setUserTheme();

		setHasMounted(true);

		if (!isAdminPageRequest) {
			const promise = dispatch(getUserById());

			promise.then(({ response }) => {
				if (response?.hasOwnProperty('isUserPreferenceRequested')) {
					setRedirectToSettings(response.isUserPreferenceRequested);
				}
			});

			promise.catch((error) => {
				if (error.response && error.response.data) {
					if (error.response.data.errors?.Error[0]?.substr(0, 4) === '1001') {
						dispatch(raiseJSValidationError('default'), true);
					}
				}
			});
		} else {
			dispatch(getAdminUserById());
		}
	}, []);

	useEffect(() => {
		if (user?.isUserPreferenceRequested !== undefined) {
			setRedirectToSettings(user.isUserPreferenceRequested);
			setUserTheme();
		}

		if(user){
			userRef.current = user; // store user in ref to avoid re-render and ensure that user is available when adobe analytics is initialized
		}

	}, [user]);

	useEffect(() => {
		if (adobeAnalyticsEnabled && userRef.current && user?.emailAddress && Object.keys(user).length !== 0) {
			dispatch(adobeAnalytics.initAdobeAnalytics(user?.emailAddress, true, envConfig.adobeAnalytics.defaultDataLayer))
			.finally(() => {
			setRoutes(getRoutes);
    		});
 		 }
		 else if (!adobeAnalyticsEnabled && userRef.current ) {
			setRoutes(getRoutes);
		 }
	}, [userRef.current]);

	// Generate redirect routes when user needs to complete settings
	const getRedirectRoutes = () => {
		if (!isAdminPageRequest && redirectToSettings) {
			return [
				<Route 
					key="redirect-root"
					exact 
					path="/" 
					render={() => <Redirect to="/settings" />} 
				/>
			];
		}
		return [];
	};

	let isDark = false;
	if (user?.theme && user.theme.toLowerCase() === Theme.Dark) {
		isDark = true;
	}

	const getRoutes = useMemo(() => {
		return [
		 {
		  path: '/dryDock',
		  component: DryDock
		 },
		 {
		  path: '/settings',
		  component: adobeAnalytics.TrackPageView(UserSettings, pageViewArgs(pageNames.userSettings, siteSections.userSettings))
		 },
		 {
		  path: '/projects',
		  component: adobeAnalytics.TrackPageView(Projects, pageViewArgs(pageNames.myProjects, siteSections.dashboard, true))
		 },
		 {
		  path: '/project',
		  component: Project,
		  isExact: true
		 },
		 {
		  path: '/project/:projectId?',
		  component: adobeAnalytics.TrackPageView(Project, pageViewArgs(pageNames.editProject, siteSections.projects))
		 },
		 {
		  path: '/projectsummary/:projectId?/:activeTab?',
		  component:ProjectSummary
		 },
		 {
		  path: '/',
		  component: adobeAnalytics.TrackPageView(Projects, pageViewArgs(pageNames.myProjects, siteSections.dashboard, true))
		 }
		];
	   }, []);

	const adminJsx = (
		<Fragment>
			<Route exact={false} key={adminRoute.path} path={adminRoute.path}>
				<Nav />
				<Main>
					<LoaderContainer isLoading={isLoading}>
						{hasMounted && <AdminPortal />}
					</LoaderContainer>
				</Main>
			</Route>
		</Fragment>
	);

	const economicsJsx = (
		<Fragment>
			<Nav />
			<Main>
				<LoaderContainer isLoading={isLoading}>
					<Switch>
						{getRedirectRoutes()}
						{routes.map(({ path, isExact, component }) => {
							return (
								<Route
									exact={isExact}
									key={path}
									path={path}
									component={component}
								/>
							);
						})}
					</Switch>
				</LoaderContainer>
			</Main>
		</Fragment>
	);

	return (
		<ThemeProvider theme={getTheme(isDark)}>
			<section className="root">
				<Errors />
				<Suspense fallback={<></>}>
					{!isAdminPageRequest ? economicsJsx : adminJsx}
				</Suspense>
			</section>
		</ThemeProvider>
	);
};

export default hot(module)(App);
